import { MerchantValidModel, ReimbursementDetailsModel } from "#/lib/carrot-api/Models"
import Helpers from "#/utils/Helpers"
import { fetchWrapper } from "#/lib/FetchWrapper/FetchWrapper"
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'uuid... Remove this comment to see the full error message
import { v4 as uuid } from "uuid"
import { CarrotErrorCodes } from "#/utils/CarrotErrors"
import { reportError } from "#/utils/ErrorReporting"
import { HttpErrors } from "#/utils/HttpErrors"
import { AboutYou, CarrotPlan, CarrotPlanEmployeeSettings, CarrotPlanStepRequest } from "../../types/carrotPlanTypes"
import ResponseError from "../../types/responseError"
import { HttpStatusCodes } from "../../utils/HttpStatusCodes"
import { BenefitEnrollmentStatus } from "../../types/benefitEnrollmentStatus"
import { InAppMessagingThread, Message, UpdatedThread } from "../../components/messaging/inAppMessagingTypes"
import { HelpCenterSsoResponse } from "../../types/zendeskTypes"
import { CreateAccount } from "./types/CreateAccount"
import { UserConditionSubset, UserInfo } from "./types/UserInfo"
import { SendTalkToCarrotMessagePayload } from "../../components/talk-to-carrot/send-message/SendMessage"
import { SendTalkToCarrotSignUpMessagePayload } from "../../components/signup-get-help/context/SignUpGetHelpContext"
import { MemberInternationalAddress, MemberUSAddress } from "./types/Address"
import { BankDetailsData } from "./types/Bank"
import { Benefit } from "./types/Benefit"
import { PhoneSupportNumber } from "./types/PhoneNumber"
import { CountryCode } from "#/utils/CountryCode"
import { IvfRequirementsInfo } from "./types/IvfRequirementsInfo"
import { SocialDeterminantsOfHealth } from "../../types/socialDeterminantsOfHealth"
import { MemberProviderSelection, UpsertMemberProviderSelectionRequest } from "../../types/memberProviderSelection"
import Settings from "#/utils/CarrotConfig"
import { MedicalInfo } from "#/types/medicalInfo"
import { FindAccountByExternalEmployeeIdPayload } from "#/components/sign-up-find-account-company-specific/context/FindAccountByExternalEmployeeIdContainer"
import { MemberBookmarkedContentItem } from "#/components/read/hooks/useMemberBookmarkedContent"
import { MemberContentFeedbackItem, CreateFeedbackRequest } from "#/components/read/hooks/useMemberContentFeedback"
import { SignUpFirstLastDateOfBirthPayload } from "#/components/signup-find-account/context/SignUpFindAccountContext"
import { CarrotMatchInteractionPayload } from "#/components/carrot-match-fertility-clinic-precision-matching/utils/carrotMatchTypes"
import {
  SignUpFindMemberByZipCodePayload,
  SignUpVerifyZipCodePayload
} from "#/components/signup-share-zip-code/context/SignUpShareZipCodeContainer"
import {
  CarrotCompanionQueryResult,
  HasUnreadMessagesFromCarrotCompanion
} from "#/components/care-team/hooks/useCarrotCompanion"
import { MemberUserFundsAdministration } from "#/lib/carrot-api/types/MemberUserFundsAdministration"
import { MemberUserCountry } from "#/lib/carrot-api/types/MemberUserCountry"
import { MemberUserCompany } from "#/lib/carrot-api/types/MemberUserCompany"
import { TwilioTokenResponse } from "#/components/talk-to-carrot/VoiceWidget/TwilioVoiceWidget"
import { MemberIdCardDetails } from "#/components/member-id/hooks/useGetMemberIdInfo"
import { RegisteringUser } from "#/lib/carrot-api/types/RegisteringUser"
import { RegisteringPartner } from "#/lib/carrot-api/types/RegisteringPartner"
import { CareTeamExpertResponse } from "#/components/care-team/hooks/useCareTeamExperts"
import { CompanyAdministrations } from "#/components/talk-to-carrot/hooks/useCompanyAdministration"
import { SprintsAuthorizationResponse, SprintsCandidacyResponse } from "#/services/user/hooks/useSprintsQuery"
import { PartnerEligibilityStatus } from "#/utils/PartnerEligibilityStatus"
import { ProviderFinderNetworkCustomization } from "#/lib/carrot-api/types/ProviderFinderNetworkCustomization"
import {
  PreAuthAnswersPayload,
  PreAuthAnswersResponse
} from "#/components/carrot-card-cost-share/state/usePreAuthAnswers"
import { ExistingPreAuthsResponse } from "#/components/carrot-card-cost-share/state/useExistingPreAuth"
import { PreAuthPayload, PreAuthResponse } from "#/components/carrot-card-cost-share/state/useCreatePreAuth"
import { PreAuthEligibilityResponse } from "#/components/carrot-card-cost-share/state/usePreAuthEligibility"
import { MemberJourneyData } from "#/types/memberJourney"
import { Reimbursement } from "#/types/reimbursement"
import { TelemedicineForPediatricsEligibility } from "#/components/telemedicine/pediatrics/hooks/useTelemedicineForPediatricsActivation"

async function processJsonResponse<T>(response: any, correlationId: any): Promise<T> {
  if (response.status === HttpStatusCodes.OK) {
    const responseText = await response.text()
    return JSON.parse(responseText)
  }
  throw new ResponseError(response, correlationId)
}

function buildRequestOptions(method: string, body?: any): { options: RequestInit; correlationId: string } {
  const correlationId = uuid()
  const headers = new Headers()
  headers.append("X-Request-ID", correlationId)
  headers.append("X-React-App-Version", __APP_VERSION__ || "local")
  headers.append("Content-Type", "application/json")

  return {
    options: {
      credentials: "include",
      body: body ? JSON.stringify(body) : null,
      headers,
      method: method
    },
    correlationId
  }
}

const clientMethods = {
  apiV2PostJson: async function (path: any, payload: any) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify(payload)
    const response = await fetchWrapper(`${Settings.CARROT_BACKEND_URL}/api/v2/${path}`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    try {
      return JSON.parse(await response.text())
    } catch {
      return null
    }
  },

  apiV2RequestWithRedirect: async function (request: any) {
    let response
    try {
      response = await request()
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        Helpers.browserRedirect(`${window.location.pathname}?errorCode=${CarrotErrorCodes.NOT_AUTHENTICATED}`)
        return
      }
      throw error
    }
    return response
  },

  // @ts-expect-error TS(7006) FIXME: Parameter 'payload' implicitly has an 'any' type.
  apiV2JsonWithRedirect: async function (path: any, method: any, payload = null) {
    return this.apiV2RequestWithRedirect(async () => {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const body = payload ? JSON.stringify(payload) : null

      return fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
        method,
        headers,
        body,
        credentials: "include"
      })
    })
  },

  apiV2GetJson: async function (path: any) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
      method: "GET",
      headers,
      credentials: "include"
    })

    const responseText = await response.text()
    try {
      return JSON.parse(responseText)
    } catch {
      return null
    }
  },

  // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection in the language menu on Sign Up pages. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
  signUp: async function (email: any, preferredLocale: any) {
    // eslint-disable-next-line no-restricted-syntax
    return this.apiV2PostJson("sign-up/verify", { email, preferredLocale })
  },

  signUpFirstLastDateOfBirth: async function ({
    email,
    firstName,
    lastName,
    dateOfBirth,
    // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
    preferredLocale
  }: SignUpFirstLastDateOfBirthPayload) {
    return this.apiV2PostJson("sign-up/find-account", {
      email,
      firstName,
      lastName,
      dateOfBirth,
      // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
      preferredLocale
    })
  },

  signUpExternalEmployeeId: async function ({
    externalEmployeeId,
    parentCompanyId,
    dateOfBirth,
    // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
    preferredLocale
  }: FindAccountByExternalEmployeeIdPayload) {
    return this.apiV2PostJson("sign-up/find-account-by-employee-id", {
      parentCompanyId,
      externalEmployeeId,
      dateOfBirth,
      // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
      preferredLocale
    })
  },

  signUpAddEmailToAccount: async function (email: any, firstName: any, lastName: any, dateOfBirth: any) {
    return this.apiV2PostJson("sign-up/email", { email, firstName, lastName, dateOfBirth })
  },

  signUpAddEmailToAccountByExternalEmployeeId: async function (
    email: any,
    externalEmployeeId: any,
    parentCompanyId: any,
    dateOfBirth: any
  ) {
    return this.apiV2PostJson("sign-up/external-id-add-email", {
      email,
      externalEmployeeId,
      parentCompanyId,
      dateOfBirth
    })
  },

  signUpVerifyZipCode: async function ({ firstName, lastName, dateOfBirth, zipCode }: SignUpVerifyZipCodePayload) {
    return this.apiV2PostJson("sign-up/verify-zip-code", {
      firstName,
      lastName,
      dateOfBirth,
      zipCode
    })
  },

  signUpFindMemberByZipCode: async function ({
    firstName,
    lastName,
    dateOfBirth,
    zipCode,
    // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
    preferredLocale
  }: SignUpFindMemberByZipCodePayload) {
    return this.apiV2PostJson("sign-up/find-account-with-zip-code", {
      firstName,
      lastName,
      dateOfBirth,
      zipCode,
      // eslint-disable-next-line no-restricted-syntax -- This is the user's preferredLocale based on their selection during registration. Carrot-app's LocaleService will then determine if they can use the selected language once they sign up.
      preferredLocale
    })
  },

  samlNoEmailOnboardingAddEmailToAccount: async function (email: any) {
    return this.apiV2PostJson("sign-up/saml-no-email-onboarding-add-email", email)
  },

  IsValidSamlNoEmailOnboardingMember: async function () {
    return this.apiV2GetJson("sign-up/is-valid-saml-no-email-onboarding-member")
  },

  logout: async function () {
    return this.apiV2PostJson("auth/logout", {})
  },

  sendPartnerInvite: async function (isConsentChecked: any) {
    return this.apiV2PostJson("partner/send-invite", { isConsentChecked })
  },

  resendPartnerInvite: async function () {
    return this.apiV2PostJson("partner/resend-invite")
  },

  removePartnerAccess: async function () {
    return this.apiV2JsonWithRedirect("partner/delete-account", "DELETE")
  },

  getPartnerInviteStatus: async function () {
    return this.apiV2GetJson("partner/status", {})
  },

  getPartnerEmailOptIn: async function () {
    return this.apiV2GetJson("partner/email-opt-in")
  },

  updatePartnerEmailOptIn: function (partnerEmailOptIn: any) {
    return this.apiV2JsonWithRedirect("partner/email-opt-in", "PATCH", { partnerEmailOptIn })
  },

  getPartnerEligibility: async function (): Promise<{ eligibilityStatus: PartnerEligibilityStatus }> {
    return this.apiV2GetJson("users/me/partner/eligibility")
  },
  getPartnerExpenseEligibility: async function (): Promise<{
    legalPartnerEligible: boolean
    nonLegalPartnerEligible: boolean
  }> {
    return this.apiV2GetJson("users/me/partner/expense-eligibility")
  },

  getPartnerScheduledServices: async function () {
    return this.apiV2GetJson("users/me/partner/scheduled-services")
  },

  sendTalkToCarrotMessage: async function (payload: SendTalkToCarrotMessagePayload) {
    return this.apiV2PostJson("talk-to-carrot/send-talk-to-carrot-message", payload)
  },

  sendTalkToCarrotSignUpMessage: async function (payload: SendTalkToCarrotSignUpMessagePayload) {
    return this.apiV2PostJson("talk-to-carrot-signup/get-help", payload)
  },

  lookup: async function (email: any) {
    return await this.apiV2PostJson("users/lookup", { email })
  },

  getIsUserLoggedIn: async function () {
    return this.apiV2GetJson("users/is-logged-in")
  },

  login: async function (email: any, password: any) {
    return await this.apiV2PostJson("auth/login", { email, password })
  },

  openIdLogin: async function (email: any, password: any) {
    return await this.apiV2PostJson("auth/openid-login", { email, password })
  },

  multiFactorAuthLogin: async function (email: string, password: string, rememberDeviceTimestamp: string) {
    return this.apiV2PostJson("auth/multi-factor-auth/login", { email, password, rememberDeviceTimestamp })
  },

  multiFactorAuthValidateCode: async function ({ code, rememberDevice }: { code: string; rememberDevice: boolean }) {
    return this.apiV2PostJson("auth/multi-factor-auth/validate-code", { code, rememberDevice })
  },

  sendNewMultiFactorAuthCode: async function () {
    return this.apiV2PostJson("auth/multi-factor-auth/new-code")
  },

  validateResetPasswordGuid: async function (passwordResetGuid: any) {
    return this.apiV2PostJson("auth/validate-reset-guid", {
      passwordResetGuid
    })
  },

  resetPassword: async function (password: any, confirmPassword: any, passwordResetGuid: any) {
    return this.apiV2PostJson("auth/reset-password", {
      password,
      confirmPassword,
      passwordResetGuid
    })
  },

  sendPersonalEmailVerificationEmail: async function () {
    return await this.apiV2PostJson("email-verification/send-verification-email", null)
  },

  verifyPersonalEmail: async function (personalEmailVerificationToken: any) {
    const response = await this.apiV2PostJson("email-verification/verify-email", { personalEmailVerificationToken })
    return response
  },

  getPersonalEmailVerified: async function () {
    return await this.apiV2GetJson("email-verification/personal-email-verified")
  },

  removePersonalEmail: async function () {
    return await this.apiV2JsonWithRedirect("email-verification/remove-personal-email", "DELETE")
  },

  updatePersonalEmailAndVerification: async function (email: any) {
    return await this.apiV2JsonWithRedirect("email-verification/update-personal-email", "PATCH", { email })
  },

  submitCardExpense: async function (
    fileGuids: any,
    whoReceivedCare: any,
    expenseJourneyType: any,
    expenseJourneySubType: any,
    relatedToStorageFees: any,
    isStepchildAdoption: any,
    isInfertility: any,
    isMedicalNecessity: any,
    attestationIsMedicallyNecessary: any,
    attestationIsEligibilityRequirementMet: any,
    attestationIsMedicallyRelated: any,
    attestationIsCoveredByMedicareAu: any,
    attestationHasPrivateInsuranceAu: any,
    expenseProviderType: any,
    obgynIuiCycleCount: any,
    attestationObgynVisitRelatedToEligibleServices: any,
    attestationObgynVisitOnlyFertilityCare: any,
    memberNotes: any,
    expenseId: any
  ) {
    return this.apiV2JsonWithRedirect("statements", "POST", {
      fileGuids,
      whoReceivedCare,
      expenseJourneyType,
      expenseJourneySubType,
      relatedToStorageFees,
      isStepchildAdoption,
      isInfertility,
      isMedicalNecessity,
      attestationIsMedicallyNecessary,
      attestationIsEligibilityRequirementMet,
      attestationIsMedicallyRelated,
      attestationIsCoveredByMedicareAu,
      attestationHasPrivateInsuranceAu,
      expenseProviderType,
      obgynIuiCycleCount,
      attestationObgynVisitRelatedToEligibleServices,
      attestationObgynVisitOnlyFertilityCare,
      memberNotes,
      expenseId
    })
  },

  submitExpense: async function (
    fileGuids: any,
    eligibleReimbursementAmount: any,
    eligibleReimbursementAmountCurrencyCode: any,
    whoReceivedCare: any,
    expenseJourneyType: any,
    expenseJourneySubType: any,
    relatedToStorageFees: any,
    isStepchildAdoption: any,
    isInfertility: any,
    isMedicalNecessity: any,
    attestationIsMedicallyNecessary: any,
    attestationIsEligibilityRequirementMet: any,
    attestationIsMedicallyRelated: any,
    attestationIsCoveredByMedicareAu: any,
    attestationHasPrivateInsuranceAu: any,
    expenseProviderType: any,
    obgynIuiCycleCount: any,
    attestationObgynVisitRelatedToEligibleServices: any,
    attestationObgynVisitOnlyFertilityCare: any,
    memberNotes: any
  ) {
    return this.apiV2JsonWithRedirect("statements", "POST", {
      fileGuids,
      eligibleReimbursementAmount: eligibleReimbursementAmount,
      eligibleReimbursementAmountCurrencyCode: eligibleReimbursementAmountCurrencyCode,
      whoReceivedCare,
      expenseJourneyType,
      expenseJourneySubType,
      relatedToStorageFees,
      isStepchildAdoption,
      isInfertility,
      isMedicalNecessity,
      attestationIsMedicallyNecessary,
      attestationIsEligibilityRequirementMet,
      attestationIsMedicallyRelated,
      attestationIsCoveredByMedicareAu,
      attestationHasPrivateInsuranceAu,
      expenseProviderType,
      obgynIuiCycleCount,
      attestationObgynVisitRelatedToEligibleServices,
      attestationObgynVisitOnlyFertilityCare,
      memberNotes
    })
  },

  startPhoneVerification: async function (phoneNumber: any, countryCode: any) {
    return this.apiV2JsonWithRedirect("phoneVerification/start", "POST", {
      phoneNumber,
      countryCode
    })
  },

  registrationPhoneStandardization: async function (phoneNumberWithCountryCode: any, countryCode: any) {
    return this.apiV2JsonWithRedirect("register/standardize-phone-number", "POST", {
      phoneNumberWithCountryCode,
      countryCode
    })
  },

  registrationPhoneLocalization: async function (phoneNumberWithCountryCode: any, countryCode: any) {
    return this.apiV2JsonWithRedirect("register/localize-phone-number", "POST", {
      phoneNumberWithCountryCode,
      countryCode
    })
  },

  completePhoneVerification: async function (phoneNumber: any, code: any, countryCode: any, allowsMarketingTexts: any) {
    return this.apiV2JsonWithRedirect("phoneVerification/complete", "POST", {
      phoneNumber,
      code,
      countryCode,
      allowsMarketingTexts
    })
  },

  requestCard: async function () {
    return this.apiV2JsonWithRedirect("card/requestcard", "POST")
  },

  verifyPhoneNumber: async function (phoneNumber: any, code: any, countryCode: any) {
    return this.apiV2JsonWithRedirect("card/verification/verifyphonenumber", "POST", {
      phoneNumber,
      code,
      countryCode
    })
  },

  createCardholder: async function (address1: any, address2: any, city: any, state: any, zip: any) {
    return this.apiV2JsonWithRedirect("card/createcardholder", "POST", {
      address1,
      address2,
      city,
      state,
      zip
    })
  },

  updateMemberCardAddress: async function (addressFields: MemberUSAddress | MemberInternationalAddress) {
    return this.apiV2JsonWithRedirect("card/updatememberaddress", "POST", addressFields)
  },

  // Registration Methods
  createAccount: async function (payload: CreateAccount) {
    return this.apiV2JsonWithRedirect(`register/${payload.guid}`, "POST", payload)
  },

  checkEmailUniqueness: async function (email: any) {
    return this.apiV2PostJson("users/lookup-email", {
      email
    })
  },

  validatePassword: async function (password: any) {
    return this.apiV2JsonWithRedirect(`register/verify-password`, "POST", {
      password
    })
  },

  updatePassword: async function ({ currentPassword, newPassword, confirmNewPassword }: any) {
    return this.apiV2PostJson("auth/update-password", {
      currentPassword,
      newPassword,
      confirmNewPassword
    })
  },

  getRegisteringUser: async function (guid: string): Promise<RegisteringUser> {
    try {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const response = await fetchWrapper(`${this.baseUrl}/api/v2/register/${guid}`, {
        method: "GET",
        headers,
        credentials: "include"
      })
      const responseText = await response.text()
      const responseJson = JSON.parse(responseText)

      return responseJson
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        Helpers.browserRedirect(`/?errorCode=${CarrotErrorCodes.EMPLOYEE_ALREADY_REGISTERED}`)
        return null
      }

      // Catching issue with CORS and 404s as well, which both are not actionable
      if ([HttpErrors.NOT_FOUND, "FetchError"].includes(error.name)) {
        Helpers.browserRedirect("/")
        return null
      }
      reportError(error)
    }
  },

  getRegisteringPartner: async function (guid: string): Promise<RegisteringPartner> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/register/partner/${guid}`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    return processJsonResponse(response, correlationId)
  },

  createPartnerAccount: async function (
    guid: any,
    password: any,
    emailOptIn: any,
    agreeToTerms: any,
    agreeToPolicy: any,
    agreeToHealthData: any
  ) {
    return this.apiV2JsonWithRedirect(`register/partner/${guid}`, "POST", {
      guid,
      password,
      emailOptIn,
      agreeToTerms,
      agreeToPolicy,
      agreeToHealthData
    })
  },

  // API V2 File Upload Request

  uploadReimbursementFile: async function (file: any, guid: string, fileCategory: string) {
    const response = await this.apiV2RequestWithRedirect(async () => {
      const body = new FormData()
      body.append("file", file)
      body.append("guid", guid)
      body.append("fileCategory", fileCategory)

      return fetchWrapper(this.baseUrl + "/api/v2/statements/files", {
        method: "POST",
        body,
        credentials: "include"
      })
    })

    const responseJson = await response.json()
    // eslint-disable-next-line no-prototype-builtins
    if (!responseJson.hasOwnProperty("fileGuid")) {
      throw new Error(`Response does not have field "fileGuid": ${JSON.stringify(responseJson)}`)
    }
    return responseJson.fileGuid
  },

  uploadAdditionalFiles: async function (expenseId: number, fileGuids: string[], memberNotes: string) {
    return this.apiV2JsonWithRedirect(`statements/${expenseId}`, "PUT", {
      fileGuids,
      memberNotes
    })
  },

  activateCard: function () {
    return this.apiV2JsonWithRedirect("card/activate", "POST", {})
  },

  getCardDetails: async function () {
    const response = await this.apiV2JsonWithRedirect("card/details", "GET")
    return await response.json()
  },

  getCurrentUser: async function (): Promise<UserInfo> {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return responseJson
  },

  getCurrentUserConditionsSubset: async function (): Promise<UserConditionSubset> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/conditions-subset"), options)
    return processJsonResponse(response, correlationId)
  },

  getReimbursements: async function (): Promise<Reimbursement[]> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/reimbursements"), options)
    return processJsonResponse<Reimbursement[]>(response, correlationId)
  },

  getCurrentUserRole: async function () {
    return await this.apiV2GetJson("users/role")
  },

  getMemberEnrollmentHistory: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/enrollment-history`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    try {
      return JSON.parse(responseText)
    } catch {
      return null
    }
  },

  checkIfMerchantValid: async function (token: any) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/merchant/isValid/${token}`, {
      method: "GET",
      headers
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new MerchantValidModel(), responseJson)
  },

  approveMerchant: async function (token: any) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    await fetchWrapper(`${this.baseUrl}/api/v2/merchant/approve/${token}`, {
      method: "POST",
      headers
    })
  },

  getReimbursementDetails: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/reimbursement-details`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new ReimbursementDetailsModel(), responseJson)
  },

  verifyMemberInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-member-insurance", null)
  },

  verifyPartnerInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-partner-insurance", null)
  },

  updateUser: function (userInfo: Partial<UserInfo>): Promise<Response> {
    return this.apiV2JsonWithRedirect("users/me", "PATCH", userInfo)
  },

  updateUserInsuranceInfo: function (userInfo: Partial<UserInfo>): Promise<Response> {
    return this.apiV2JsonWithRedirect("users/me/insurance-info", "PATCH", userInfo)
  },

  forgotPassword: async function (email: any) {
    return this.apiV2PostJson("users/forgotPassword", { email })
  },

  getHelpCenterSsoPayload: async function (isRetry = false): Promise<HelpCenterSsoResponse> {
    let path = "users/zendesk/help-center-sso"
    if (isRetry) {
      path += "?isRetry=true"
    }
    const response = await this.apiV2JsonWithRedirect(path, "GET")
    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  startRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info", "POST")
    return response
  },

  startRenewalRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info/renewal", "POST")
    return response
  },

  startJourneyChangeRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info/change-journey", "POST")
    return response
  },

  startCheckInExperienceRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info/check-in", "POST")
    return response
  },

  updateRoutingInfo: async function (payload: any) {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info", "PATCH", payload)
    return response
  },

  getCarrotPlansRoutingInfo: async function () {
    const response = await this.apiV2GetJson("users/me/routing-info")
    return response
  },

  getMostRecentlyCompletedRoutingInfo: async function () {
    const response = await this.apiV2GetJson("users/me/routing-info/most-recently-completed")
    return response
  },

  getInProgressCarrotPlanRequestType: async function () {
    try {
      const response = await this.apiV2GetJson("users/me/routing-info/in-progress-request-type")
      return response
    } catch (err) {
      // apiV2GetJson throws SyntaxError when body is empty
      if (err instanceof SyntaxError) {
        return null
      }
      throw err
    }
  },

  getRegistrationJourney: async function () {
    const response = await this.apiV2GetJson("users/me/routing-info/registration-journey")
    return response
  },

  getSocialDeterminantsOfHealthInRouting: async function (routingInfoId: number) {
    return await this.apiV2GetJson(`users/me/sdoh/${routingInfoId}`)
  },

  setSocialDeterminantsOfHealth: async function (payload: SocialDeterminantsOfHealth) {
    const response = await this.apiV2JsonWithRedirect(`users/me/sdoh`, "POST", payload)
    return response
  },

  getMemberProviderSelectionByRoutingInfoId: async function (routingInfoId: number): Promise<MemberProviderSelection> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(
      this._buildApiV2Url(`users/me/provider-selections/routing-info/${routingInfoId}`),
      options
    )
    return processJsonResponse<MemberProviderSelection>(response, correlationId)
  },

  upsertMemberProviderSelection: async function (
    payload: UpsertMemberProviderSelectionRequest
  ): Promise<MemberProviderSelection> {
    const response = await this.apiV2PostJson("users/me/provider-selections", payload)
    return response
  },

  // @ts-expect-error TS(7006) FIXME: Parameter 'payload' implicitly has an 'any' type.
  startMedicalInfoFlow: async function (payload = null) {
    const response = await this.apiV2JsonWithRedirect("users/me/medical-info", "POST", payload)
    return response
  },

  updateMedicalInfo: async function (payload: any) {
    const response = await this.apiV2JsonWithRedirect("users/me/medical-info", "PATCH", payload)
    return response
  },

  getCarrotPlansMedicalInfo: async function () {
    const response = await this.apiV2GetJson("users/me/medical-info")
    return response
  },

  getAboutYou: async function () {
    const response = await this.apiV2GetJson("users/me/about-you")
    return response
  },

  updateAboutYou: async function (aboutYouModel: Partial<AboutYou>) {
    return this.apiV2JsonWithRedirect("users/me/about-you", "PATCH", aboutYouModel)
  },

  getInAppMessagingThreads: async function () {
    return await this.apiV2GetJson("in-app-messaging/threads")
  },

  createThread: async function (subject: any, message: any, viaFollowUpSourceId: any) {
    const response = await this.apiV2PostJson("in-app-messaging/threads", {
      subject,
      message,
      viaFollowUpSourceId: viaFollowUpSourceId ?? undefined
    })
    return response
  },

  updateThread: async function (threadId: any, message: any): Promise<UpdatedThread> {
    const response = await this.apiV2JsonWithRedirect(`in-app-messaging/threads/${threadId}`, "PUT", {
      message
    })
    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  getThreadMessages: async function (threadId: any) {
    return await this.apiV2GetJson(`in-app-messaging/threads/${threadId}/messages`)
  },

  patchThread: async function (threadId: any, payload: any) {
    return await this.apiV2JsonWithRedirect(`in-app-messaging/threads/${threadId}`, "PATCH", payload)
  },

  enrollUserBenefitEnrollment: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/benefit-enrollment", "POST")
    return response
  },

  getBenefitEnrollmentPreview: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/benefit-enrollment/preview", "GET")
    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  getBenefitEnrollmentStatus: async function (): Promise<BenefitEnrollmentStatus> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/enrollment-status"), options)
    return await processJsonResponse<BenefitEnrollmentStatus>(response, correlationId)
  },

  deactivateMedicareEnrollment: async function (): Promise<void> {
    const { options, correlationId } = buildRequestOptions("PUT")
    const response = await fetch(this._buildApiV2Url("users/me/medicare-enrollment/deactivate"), options)
    if (response.status !== HttpStatusCodes.OK) {
      throw new ResponseError(response, correlationId)
    }
  },

  addMedicareEnrollment: function (medicareId: string): Promise<Response> {
    return this.apiV2PostJson("users/me/medicare-enrollment", { medicareId: medicareId })
  },

  unPoseAsMember: async function (): Promise<Response> {
    const { options, correlationId } = buildRequestOptions("POST")
    const response = await fetch(this._buildApiV2Url("member-pose/un-pose"), options)
    return await processJsonResponse(response, correlationId)
  },

  // Carrot plans
  getCarrotPlan: async function (): Promise<CarrotPlan> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/carrot-plan"), options)
    return processJsonResponse<CarrotPlan>(response, correlationId)
  },

  republishCarrotPlan: async function (employeeId: any) {
    return this.apiV2JsonWithRedirect("users/me/carrot-plan/republish", "POST", employeeId)
  },

  setDoneWithCarrot: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/settings/done-with-carrot")
  },

  requestInitialCarrotPlan: async function () {
    return await this.apiV2JsonWithRedirect("users/me/carrot-plan/initial-carrot-plan-chat-requested", "POST")
  },

  requestJourneyChangeCarrotPlan: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/request-journey-change-carrot-plan")
  },

  requestCheckInCarrotPlan: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/request-check-in-carrot-plan")
  },

  requestNewCaseRateRenewalCarrotPlan: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/request-case-rate-renewal-carrot-plan")
  },

  setNonStaleCarrotPlanOnAnniversaryCheck: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/non-stale-unlock")
  },

  setCarrotPlanRenewalDismissed: async function () {
    return this.apiV2PostJson("users/me/carrot-plan/case-rate-renewal-dismissed")
  },

  getCaseRateRenewalStatus: async function () {
    return this.apiV2GetJson("users/me/carrot-plan/case-rate-renewal-status")
  },

  getDaysSinceLastUnlock: async function () {
    return this.apiV2GetJson("users/me/carrot-plan/days-since-unlock")
  },

  startCarrotPlansEnrollment: function () {
    return this.apiV2JsonWithRedirect("users/me/carrot-plans/routing/start", "POST")
  },

  removePartner: function () {
    return this.apiV2JsonWithRedirect("users/me/partner", "DELETE")
  },

  getCarrotPlanEmployeeSettings: async function (): Promise<CarrotPlanEmployeeSettings> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/carrot-plan/settings/employees"), options)
    return processJsonResponse<CarrotPlanEmployeeSettings>(response, correlationId)
  },
  updateCarrotPlanStep: async function ({
    stepId,
    completed,
    markedNotRelevant
  }: CarrotPlanStepRequest): Promise<void> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("X-React-App-Version", __APP_VERSION__ || "local")
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify({
      stepId: stepId,
      completed: completed,
      markedNotRelevant: markedNotRelevant
    })
    await fetch(`${this.baseUrl}/api/v2/users/me/carrot-plan/steps/${stepId}`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })
  },

  getCarrotPlanRequestStatus: async function () {
    return this.apiV2GetJson("users/me/carrot-plan/request-status")
  },

  _buildApiV2Url: function buildApiV2Url(path: string): string {
    return `${this.baseUrl}/api/v2/${path}`
  },

  triggerQleEnrollment: async function (): Promise<boolean> {
    const { options } = buildRequestOptions("POST")
    const response = await fetch(this._buildApiV2Url("enrollments/qle"), options)
    return response.ok
  },

  triggerInsuranceEdit: async function (): Promise<boolean> {
    const { options } = buildRequestOptions("POST")
    const response = await fetch(this._buildApiV2Url("enrollments/clear/insurance"), options)
    return response.ok
  },

  getBenefitEnrollment: async function () {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/benefit-enrollment"), options)
    return await processJsonResponse(response, correlationId)
  },

  getZendeskMobileAuthToken: async function () {
    const { options } = buildRequestOptions("GET")
    const response = await this.apiV2RequestWithRedirect(async () =>
      fetchWrapper(this._buildApiV2Url("users/zendesk/mobile/support-center-mobile-auth-token"), options)
    )
    return response.text()
  },

  createZendeskTicketForCPMedicalQuestions: async function (stepId: number) {
    await this.apiV2PostJson(`users/me/carrot-plan/medical-questions-completed/${stepId}`, "POST")
  },

  getAirwallexScaAuthorizationCode: async function () {
    return this.apiV2GetJson("airwallex/authorization-code/sca")
  },

  createAirwallexPanToken: async function (scaToken: string) {
    return this.apiV2JsonWithRedirect("airwallex/issuing/pantoken", "POST", { scaToken })
  },

  markScaSetupAsCompleted: async function () {
    await this.apiV2PostJson("card/sca-setup-completed", null)
  },

  getBankDetailsSchema: async function () {
    const { options } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("bank-details/schema"), options)
    return response.json()
  },

  getBankDetails: async function (): Promise<BankDetailsData> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/bank-details"), options)

    return processJsonResponse(response, correlationId)
  },

  updateBankDetails: async function (bankDetails: any) {
    const response = await this.apiV2JsonWithRedirect("users/me/bank-details", "PATCH", bankDetails)
    return response.json
  },

  getBenefit: async function (): Promise<Benefit> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("benefits/me"), options)

    return processJsonResponse(response, correlationId)
  },

  getFundsAdministration: async function (): Promise<MemberUserFundsAdministration> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("funds-administration/me"), options)

    return processJsonResponse(response, correlationId)
  },

  getMemberUserCountry: async function (): Promise<MemberUserCountry> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("country/me"), options)

    return processJsonResponse(response, correlationId)
  },

  getMemberUserCompany: async function (): Promise<MemberUserCompany> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("company/me"), options)

    return processJsonResponse(response, correlationId)
  },
  // This could probably benefit from ENUMS
  getCompanyComplianceConfigLineItemFilter: async function (): Promise<string> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("company-compliance-config/line-item-filter"), options)

    return processJsonResponse(response, correlationId)
  },

  getPhoneSupportNumber: async function ({
    companyId,
    countryCode
  }: {
    companyId: number
    countryCode: CountryCode
  }): Promise<PhoneSupportNumber> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url(`phone-support/${companyId}/${countryCode}`), options)

    return processJsonResponse(response, correlationId)
  },

  createMemberAction: async function (memberActionTaken: string, metaData?: any) {
    await this.apiV2PostJson(`member-action-tracking/add-member-action`, {
      memberActionTaken: memberActionTaken,
      metaData: metaData
    })
  },

  createRegistrationMemberAction: async function (registrationGuid: string, memberActionTaken: string) {
    await this.apiV2PostJson(`member-action-tracking/add-registration-member-action/${registrationGuid}`, {
      registrationGuid: registrationGuid,
      memberActionTaken: memberActionTaken
    })
  },

  getAllEnabledLocales: async function (): Promise<string[]> {
    return await this.apiV2GetJson("locales")
  },

  getAllEnabledLocalesForRegisteringUser: async function ({ employeeId }: { employeeId?: number }): Promise<string[]> {
    return await this.apiV2GetJson(`locales/registering/${employeeId}`)
  },

  getEnabledLocalesForCurrentUser: async function (): Promise<string[]> {
    return await this.apiV2GetJson("locales/me")
  },

  getSignatureOrSurveyLinkClickExists: async function () {
    return this.apiV2GetJson("survey/get-signature-survey-event-exists")
  },

  getActiveSignatures: async function () {
    return this.apiV2GetJson("signatures/active")
  },

  getProviderFinderNetworkCustomization: async function (): Promise<ProviderFinderNetworkCustomization> {
    return this.apiV2GetJson("provider-finder/network-customization/me")
  },

  createSurveyLinkView: async function () {
    return this.apiV2PostJson("survey/track-link-view?bannerVariant=1", {})
  },

  createSurveyLinkClick: async function () {
    return this.apiV2PostJson("survey/track-link-click?bannerVariant=1", {})
  },

  createSurveyBannerCloseClick: async function () {
    return this.apiV2PostJson("survey/track-close-click?bannerVariant=1", {})
  },

  getTelemedicineConsentTime: async function () {
    return this.apiV2GetJson("telemedicine/menopause/consent")
  },

  createOrUpdateTelemedicineConsentTime: async function () {
    await this.apiV2PostJson("telemedicine/menopause/consent")
  },

  submitRtxConsent: async function (payload: { optIn: boolean }) {
    await this.apiV2PostJson("consents/rtx", payload)
  },

  submitFreedomFertilityConsent: async function (payload: { optIn: boolean }) {
    await this.apiV2PostJson("consents/freedom-fertility", payload)
  },

  createOrUpdateTelemedicineProfile: async function () {
    await this.apiV2PostJson("telemedicine/menopause/profile/me")
  },

  getTelemedicineAvailability: async function () {
    return this.apiV2GetJson("telemedicine/menopause/availability/me")
  },

  createTelemedicineInterest: async function () {
    await this.apiV2PostJson("telemedicine/menopause/interest")
  },

  trackTelemedicineLogin: async function () {
    await this.apiV2PostJson("telemedicine/menopause/track-login")
  },

  getTelemedicineForPediatricsEligibility: async function (): Promise<TelemedicineForPediatricsEligibility> {
    return this.apiV2GetJson("telemedicine/pediatrics/eligibility/me")
  },

  createTelemedicineForPediatricsEligibility: async function (
    email: string
  ): Promise<TelemedicineForPediatricsEligibility> {
    return this.apiV2PostJson("telemedicine/pediatrics/eligibility/me", { email })
  },

  registerTelemedicineForPelvicFloorTherapy: async function (): Promise<{ url: string }> {
    return this.apiV2PostJson("telemedicine/pelvic-floor/registration/me")
  },

  updatePushNotificationOptInSetting: async function (request: boolean): Promise<void> {
    const { options, correlationId } = buildRequestOptions("PUT", { pushNotificationsOptIn: request })
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/push-notifications`, options)
    if (response.status !== HttpStatusCodes.OK) {
      throw new ResponseError(response, correlationId)
    }
  },

  getChannelId: async function (): Promise<number> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/channel"), options)
    if (response.status === HttpStatusCodes.NOT_FOUND) {
      return null
    }
    if (response.status !== HttpStatusCodes.OK) {
      throw new ResponseError(response, correlationId)
    }
    return response.json()
  },

  getIvfRequirementsInfo: async function (): Promise<IvfRequirementsInfo> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await this.apiV2RequestWithRedirect(async () =>
      fetchWrapper(this._buildApiV2Url("ivf-requirements/me"), options)
    )
    return processJsonResponse(response, correlationId)
  },

  getMemberMedicalInfoForCarrotMatch: async function (): Promise<MedicalInfo> {
    return this.apiV2GetJson("carrot-match/member-medical-info")
  },

  getPartnerMedicalInfoForCarrotMatch: async function (): Promise<MedicalInfo> {
    return this.apiV2GetJson("carrot-match/partner-medical-info")
  },

  saveCarrotMatchInteraction: async function (
    carrotMatchInteractionPayload: CarrotMatchInteractionPayload
  ): Promise<void> {
    return this.apiV2JsonWithRedirect("carrot-match/carrot-match-interaction", "PUT", carrotMatchInteractionPayload)
  },

  getCarrotCompanion: async function (): Promise<CarrotCompanionQueryResult> {
    return this.apiV2GetJson("carrot-companion")
  },

  getHasUnreadMessagesFromCarrotCompanion: async function (): Promise<HasUnreadMessagesFromCarrotCompanion> {
    return this.apiV2GetJson("carrot-companion/has-unread-messages")
  },

  getCarrotCompanionCarrotPlanMessage: async function (): Promise<{
    thread: InAppMessagingThread
    carrotPlanMessage: Message
  }> {
    return this.apiV2GetJson("carrot-companion/carrot-companion-message")
  },

  getTwilioToken: async function (): Promise<TwilioTokenResponse> {
    return this.apiV2GetJson("twilio/token")
  },
  getMemberId: async function (): Promise<string> {
    const result = await this.apiV2GetJson("users/me/member-id")
    return result.id
  },
  getMemberIdCardDetails: async function (): Promise<MemberIdCardDetails> {
    return this.apiV2GetJson(`users/me/member-id-card`)
  },
  getCareTeamExperts: async function (): Promise<CareTeamExpertResponse[]> {
    return this.apiV2GetJson(`care-team-experts`)
  },
  getCompanyAdministrations: async function (): Promise<CompanyAdministrations> {
    return this.apiV2GetJson("users/me/company-administrations")
  },
  getSprintsCandidacy: async function (): Promise<SprintsCandidacyResponse> {
    return this.apiV2GetJson("users/me/sprints-access/candidacy")
  },
  getSprintsAuthorization: async function (): Promise<SprintsAuthorizationResponse> {
    return this.apiV2GetJson("users/me/sprints-access/authorization")
  },
  getMemberJourneyData: async function (): Promise<MemberJourneyData> {
    return this.apiV2GetJson("users/me/member-journey")
  },
  getMemberBookmarkedContent: async function (): Promise<MemberBookmarkedContentItem[]> {
    return this.apiV2GetJson("member-bookmarked-content/me")
  },

  addMemberBookmarkedContent: async function (contentfulEntryId: string, categoryEntryId: string, contentType: string) {
    return this.apiV2PostJson(`member-bookmarked-content/me/${contentfulEntryId}`, { categoryEntryId, contentType })
  },

  removeMemberBookmarkedContent: async function (contentfulEntryId: string, categoryEntryId: string) {
    return this.apiV2JsonWithRedirect(`member-bookmarked-content/me/${contentfulEntryId}/${categoryEntryId}`, "DELETE")
  },

  getMemberContentFeedback: async function (
    contentfulEntryId: string,
    categoryEntryId: string
  ): Promise<MemberContentFeedbackItem | null> {
    return this.apiV2GetJson(`member-feedback/me/${contentfulEntryId}/${categoryEntryId}`)
  },

  createMemberContentFeedback: async function (request: CreateFeedbackRequest): Promise<MemberContentFeedbackItem> {
    return this.apiV2PostJson("member-feedback/me", request)
  },

  submitPreAuthAnswers: async function (payload: PreAuthAnswersPayload): Promise<PreAuthAnswersResponse> {
    return this.apiV2PostJson("carrot-card/pre-auth-answers", payload)
  },

  getExistingPreAuths: async function (): Promise<ExistingPreAuthsResponse> {
    return this.apiV2GetJson("carrot-card/pre-auths?active=true")
  },

  createPreAuth: async function (payload: PreAuthPayload): Promise<PreAuthResponse> {
    return this.apiV2PostJson("carrot-card/pre-auth", payload)
  },

  cancelPreAuth: async function (id: number): Promise<void> {
    return this.apiV2JsonWithRedirect(`carrot-card/pre-auth/${id}?action=cancel`, "PATCH")
  },

  getPreAuthEligibility: async function (): Promise<PreAuthEligibilityResponse> {
    return this.apiV2GetJson("carrot-card/pre-auth-eligibility")
  }
}

const CarrotClient = function (baseUrl: string) {
  this.baseUrl = baseUrl
} as any as { new (baseUrl: string): typeof clientMethods }
CarrotClient.prototype = clientMethods

export default CarrotClient
